import { MarketplaceType } from '@yoop/server-model/marketplace';
import type { TokenInfoResponse } from '@yoop/server-model/token-info';
import { OfferSubStatus } from '@yoop/server-model/token-info';
import type {
  EventDetailResponse,
  GetBidResponse,
  GetOffersUpgradabilityRequest,
  SimpleUserActivityEventResponse,
  UserActivityEventDetailResponse,
} from '@yoop/server-model/user-activity';
import { BidStatus, PriceDisplayMode } from '@yoop/server-model/user-activity';
import { dataMapFrom, isNilOrEmpty } from '@yoop/util';
import { datetime } from '@yoop/util-datetime';
import type {
  MyYoopEvent,
  MyYoopOffers,
  MyYoopRequestedEvent,
  UserProfile,
  MyPriceOffer,
  EventDetailData,
  EventTransactingTool,
} from '@yoop/whitelabel-data';
import { MyPriceOfferStatus } from '@yoop/whitelabel-data';
import { mediaFrom } from '@yoop/whitelabel-media';
import isNil from 'ramda/src/isNil';
import { commonEventDataFrom } from './event-common.helper';
import {
  getMarketplaceTypePriority,
  isMarketplaceActive,
  transactingToolFrom,
} from './marketplace.helper';
import { requestedOffersInfoFrom } from './offer.helper';
import { detailTokensFrom, pendingTokensFrom, tokensFrom } from './token.helper';

//duplicated from libs/util-token/src/lib/helper/get-tokens.ts
export const MIN_TOKENS_TO_SHOW_BULK_OPT = 8;

export const myYoopEventFrom = (
  response: SimpleUserActivityEventResponse,
  currentUser: UserProfile,
): MyYoopEvent => {
  const { tokens, extras } = tokensFrom({
    ownedTokens: response.tokenInformation,
    assignedTokens: response.assigneeTokenInformation,
    isResaleOpen: isMarketplaceActive(response.resaleMarketplace),
    currentUserProfile: currentUser,
  });

  return {
    ...commonEventDataFrom(response),
    nearestTransactingTool: transactingToolFrom(
      response.nextMarketplace,
      response.priceDisplay === PriceDisplayMode.ALL_IN_PRICE,
    ),
    extrasTokens: extras,
    pendingTokens: pendingTokensFrom(response.marketplaceInformation),
    eventAccessTokens: tokens,
  };
};

export const eventDetailDataFrom = (
  response: UserActivityEventDetailResponse,
  currentUser: UserProfile,
  upgradableOffers: Set<number>,
): EventDetailData => {
  const isResaleOpen = response.marketplaceInformation.some(
    (marketplace) =>
      marketplace.type === MarketplaceType.SECONDARY_EXCHANGE && isMarketplaceActive(marketplace),
  );
  const { tokens, extras } = detailTokensFrom({
    ownedTokens: response.tokenInformation,
    assignedTokens: response.assigneeTokenInformation,
    isResaleOpen,
    currentUserProfile: currentUser,
    upgradableOffers,
    accessControlSubMode: response.accessControlSubMode,
    //Duplicated logic from 'eventDoorsOpen'
    isDoorsOpen: datetime
      .now()
      .isBetween(response.doorsOpenDate, response.doorsCloseDate, undefined, '[)'),
    categoryDetailMap: dataMapFrom(response.categoryDetails, ({ id }) => id),
  });

  const numOfAssignableTokens =
    tokens.filter(({ canAssign }) => canAssign).length +
    extras.filter(({ canAssign }) => canAssign).length;
  const publicData = publicEventDataFrom(response);
  return {
    ...publicData,
    extrasTokens: extras,
    eventAccessTokens: tokens,
    canBulkTransfer: numOfAssignableTokens > MIN_TOKENS_TO_SHOW_BULK_OPT,
    myPriceOfferInfo: {
      ...publicData.myPriceOfferInfo,
      activeOffers: response.bids?.map(myPriceOfferFrom) ?? [],
    },
  };
};

export const publicEventDataFrom = (
  response: UserActivityEventDetailResponse | EventDetailResponse,
): EventDetailData => {
  const sortedMarketplaces =
    response.marketplaceInformation?.sort((lhs, rhs) => {
      const canBuyYoop = isNil(lhs.minimumPrice);
      if (canBuyYoop === isNil(lhs.minimumPrice)) {
        return getMarketplaceTypePriority(lhs.type) - getMarketplaceTypePriority(rhs.type);
      }
      return canBuyYoop ? 1 : -1;
    }) ?? [];
  const transactingToolsMap: Record<string, EventTransactingTool> = {};
  const childrenTransactingTools: Record<string, EventTransactingTool[]> = {};

  sortedMarketplaces.forEach((marketplace) => {
    const _item = transactingToolFrom(
      marketplace,
      response.priceDisplay === PriceDisplayMode.ALL_IN_PRICE,
    );
    if (marketplace.parentMarketplaceId) {
      childrenTransactingTools[marketplace.parentMarketplaceId] =
        childrenTransactingTools[marketplace.parentMarketplaceId] ?? [];
      childrenTransactingTools[marketplace.parentMarketplaceId].push(_item);
    } else {
      transactingToolsMap[_item.id] = _item;
    }
  });
  for (const parentId in childrenTransactingTools) {
    if (transactingToolsMap[parentId]) {
      transactingToolsMap[parentId].childrenTransactingTools = childrenTransactingTools[parentId];
    }
  }

  const transactingTools = Object.values(transactingToolsMap);
  const activeMarketplace = transactingTools.find(({ startTime, endTime }) =>
    datetime.now().isBetween(startTime, endTime),
  );

  return {
    ...commonEventDataFrom(response),
    transactingTools,
    restrictions: response.eventRestrictions?.map((restriction) => ({
      id: restriction.id,
      description: restriction.description,
      icon: restriction.icon && mediaFrom(restriction.icon),
      url: restriction.url,
      secondaryDescription: restriction.secondaryDescription,
    })),
    canSeeInfo: !isNilOrEmpty(response.eventRestrictions),
    canBulkTransfer: false,
    canBuyMoreYoop: activeMarketplace?.canBuyYoop,
    canBuyMoreMerch: activeMarketplace?.canBuyMerchandise,
    canBuyMoreAddons: activeMarketplace?.canBuyAddons,
    canSeeReceipt: response.tokenInformation?.some(
      ({ offerResponse }) =>
        offerResponse?.offerSubStatus !== OfferSubStatus.REFUNDED && offerResponse?.amount > 0,
    ),
    myPriceOfferInfo: {
      enabled: response.exchangeInfo?.exchangeEnabled ?? false,
      availableCategories:
        response.exchangeInfo?.inventoryCategories.map(({ inventoryCategory, minBidPrice }) => ({
          id: inventoryCategory.id.toString(),
          color: inventoryCategory.displaySettings.color,
          name: inventoryCategory.name,
          detail: inventoryCategory.detail,
          minPrice: minBidPrice,
        })) ?? [],
      activeOffers: [],
    },
  };
};

export const myYoopRequestedEventFrom = (
  response: SimpleUserActivityEventResponse,
): MyYoopRequestedEvent => {
  let canBidMore = false;
  if (response.exchangeInfo?.exchangeEnabled) {
    canBidMore = datetime
      .now()
      .isBetween(
        datetime.parse(response.exchangeInfo.bidSubmissionWindow?.startTime),
        datetime.parse(response.exchangeInfo.bidSubmissionWindow?.endTime),
      );
  }
  let offers: MyYoopOffers[] = [];
  let canSubmitMoreOffers = false;
  response.marketplaceInformation?.map(requestedOffersInfoFrom).forEach((requestedInfo) => {
    offers = [...offers, ...requestedInfo.offers];
    canSubmitMoreOffers = canSubmitMoreOffers || requestedInfo.canSubmitMoreOffers;
  });
  return {
    ...commonEventDataFrom(response),
    nearestTransactingTool: transactingToolFrom(
      response.nextMarketplace,
      response.priceDisplay === PriceDisplayMode.ALL_IN_PRICE,
    ),
    requestedOffers: offers,
    myPriceOffers: response.bids?.map(myPriceOfferFrom),
    canSubmitAnotherBid: canBidMore,
    canSubmitAnotherOffer: canSubmitMoreOffers,
  };
};

const myPriceOfferFrom = (offer: GetBidResponse): MyPriceOffer => ({
  id: offer.offerId,
  price: offer.price,
  description: offer.inventoryCategoryName,
  yoopCount: offer.quantity,
  categoryId: offer.inventoryCategoryId,
  expireTime: offer.expireTime,
  status: myPriceOfferStatusFrom(offer.status),
});

const myPriceOfferStatusFrom = (status: BidStatus): MyPriceOfferStatus => {
  switch (status) {
    case BidStatus.OPEN:
      return MyPriceOfferStatus.Active;
    case BidStatus.EXPIRED:
      return MyPriceOfferStatus.Expired;
    case BidStatus.CANCELLED:
      return MyPriceOfferStatus.Cancelled;
    case BidStatus.FAILED:
      return MyPriceOfferStatus.Failed;
  }
  //BidStatus.FILLED
  return undefined;
};

export const upgradeOfferRequestFrom = (
  tokens: TokenInfoResponse[],
): GetOffersUpgradabilityRequest | undefined => {
  const upgradeGroup: Record<string, number[]> = {};
  tokens?.forEach(({ offerResponse, tokenId }) => {
    if (!isNilOrEmpty(offerResponse)) {
      upgradeGroup[offerResponse.id] = upgradeGroup[offerResponse.id] ?? [];
      upgradeGroup[offerResponse.id].push(parseInt(tokenId));
    }
  });

  if (isNilOrEmpty(upgradeGroup)) {
    return undefined;
  }

  return {
    offerUpgradeOptionsRequestItems: Object.entries(upgradeGroup).map(([key, value]) => ({
      sourceOfferId: parseInt(key),
      inventoryItemIdsForUpgrade: value,
    })),
  };
};
